/**
 * ComptaNav — bandeau de navigation Comptabilité regroupé (juillet 2026).
 *
 * Remplace les 14 onglets à plat (barre à rallonge) par 5 entrées, sur le même
 * principe que GrcNav (« Dossiers » déroulant) pour rester cohérent partout :
 *
 *   Vue d'ensemble · Recettes ▾ · Dépenses ▾ · Comptabilité ▾ · Annuaire
 *
 *   Recettes ▾      : Encaissements · Factures émises · Rôle collectif ASAP · Modèles
 *   Dépenses ▾      : Dépenses · Factures reçues · Notes de frais · Types de frais
 *   Comptabilité ▾  : Livre · Journal · Dépôts banque · Postes de ventilation · Présence
 *   Annuaire        : Annuaire des tiers (entrée unique → bouton simple)
 *
 * Comme GrcNav :
 *  - le menu est rendu en position: fixed (échappe aux overflow / backdrop-blur
 *    / sticky des conteneurs parents qui rognaient le menu) ;
 *  - il se ferme au clic extérieur, au scroll et au resize ;
 *  - le bouton de groupe reste NEUTRE (pas de surlignage actif) — seul le
 *    contenu change, exactement comme les items « Dossiers » de GRC.
 *
 * L'action est un onglet interne (setTab) et non une navigation de route :
 * la Comptabilité est une page unique à onglets, contrairement à GRC.
 */
import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Settings } from 'lucide-react';
import {
  Layers, ListChecks, FileText, LayoutTemplate, ArrowUpRight, Inbox,
  Wallet, Banknote, BookOpen, CalendarDays, Users, FileStack,
} from 'lucide-react';

export type ComptaTab =
  | 'ensemble' | 'encaissements' | 'factures' | 'roles_asap' | 'modeles'
  | 'decaissements' | 'factures_recues' | 'ndf' | 'types_frais' | 'budgets'
  | 'livre' | 'journal' | 'depots' | 'postes' | 'presence' | 'tiers' | 'reglages';

type Item = { k: ComptaTab; label: string; icon: React.ComponentType<{ className?: string }> };

// Onglets accessibles à un agent comptable (accès restreint) — cohérent avec
// le cloisonnement des autres briques.
const AGENT_TABS: ComptaTab[] = ['ensemble', 'encaissements', 'journal', 'factures'];

const RECETTES: Item[] = [
  { k: 'encaissements', label: 'Encaissements', icon: ListChecks },
  { k: 'factures', label: 'Factures émises', icon: FileText },
  { k: 'roles_asap', label: 'Rôle collectif ASAP', icon: FileStack },
  { k: 'modeles', label: 'Modèles', icon: LayoutTemplate },
];
const DEPENSES: Item[] = [
  // Vague 4d (mig 247) — enveloppes par secteur, consommées par les devis (3.14).
  { k: 'budgets', label: 'Budgets', icon: Wallet },
  { k: 'decaissements', label: 'Dépenses', icon: ArrowUpRight },
  { k: 'factures_recues', label: 'Factures reçues', icon: Inbox },
  { k: 'ndf', label: 'Notes de frais', icon: ListChecks },
  { k: 'types_frais', label: 'Types de frais', icon: Layers },
];
const COMPTA: Item[] = [
  { k: 'livre', label: 'Livre', icon: Wallet },
  { k: 'journal', label: 'Journal', icon: BookOpen },
  { k: 'depots', label: 'Dépôts banque', icon: Banknote },
  { k: 'postes', label: 'Postes de ventilation', icon: Layers },
];

const ACCENT = 'oklch(var(--c-compta))';

/** Bouton d'onglet simple (Vue d'ensemble, Annuaire). */
function Tab({
  active, onClick, icon: Icon, children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-r-pill border px-3 py-1.5 text-xs font-medium transition ${
        active
          ? 'text-white'
          : 'border-border bg-transparent text-ink-muted hover:bg-surface/40 hover:text-ink'
      }`}
      style={active ? { background: ACCENT, borderColor: 'transparent' } : undefined}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {children}
    </button>
  );
}

/** Groupe déroulant (Recettes, Dépenses, Comptabilité) — reste neutre. */
function GroupMenu({
  label, items, tab, setTab, estGestion,
}: {
  label: string;
  items: Item[];
  tab: ComptaTab;
  setTab: (t: ComptaTab) => void;
  estGestion: boolean;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const visibles = items.filter((it) => estGestion || AGENT_TABS.includes(it.k));
  if (visibles.length === 0) return null;

  function toggle() {
    setOpen((o) => {
      const next = !o;
      if (next && btnRef.current) {
        const r = btnRef.current.getBoundingClientRect();
        setPos({ top: r.bottom + 4, left: r.left });
      }
      return next;
    });
  }

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      const t = e.target as Node;
      if (
        menuRef.current && !menuRef.current.contains(t) &&
        btnRef.current && !btnRef.current.contains(t)
      ) setOpen(false);
    }
    function onScrollOrResize() { setOpen(false); }
    document.addEventListener('mousedown', onClickOutside);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-r-pill border border-border bg-transparent px-3 py-1.5 text-xs font-medium text-ink-muted transition hover:bg-surface/40 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        style={{ ['--tw-ring-color' as string]: ACCENT }}
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && pos && (
        <div
          ref={menuRef}
          role="menu"
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
          className="min-w-[210px] rounded-r-md border border-border bg-surface shadow-r-lg p-1"
        >
          {visibles.map((it) => (
            <button
              key={it.k}
              role="menuitem"
              type="button"
              onClick={() => { setOpen(false); setTab(it.k); }}
              className={`flex w-full items-center gap-2 rounded-r-sm px-3 py-2 text-left text-xs transition hover:bg-surface/60 ${
                tab === it.k ? 'text-ink' : 'text-ink-muted hover:text-ink'
              }`}
              style={tab === it.k ? { color: ACCENT } : undefined}
            >
              <it.icon className="h-3.5 w-3.5" aria-hidden="true" />
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ComptaNav({
  tab, setTab, estGestionCompta,
}: {
  tab: ComptaTab;
  setTab: (t: ComptaTab) => void;
  estGestionCompta: boolean;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-border pb-3">
      <Tab active={tab === 'ensemble'} onClick={() => setTab('ensemble')} icon={Layers}>
        Vue d'ensemble
      </Tab>

      <GroupMenu label="Recettes" items={RECETTES} tab={tab} setTab={setTab} estGestion={estGestionCompta} />
      <GroupMenu label="Dépenses" items={DEPENSES} tab={tab} setTab={setTab} estGestion={estGestionCompta} />
      <GroupMenu label="Comptabilité" items={COMPTA} tab={tab} setTab={setTab} estGestion={estGestionCompta} />

      {/*
        Planning visible par TOUS, agent compris. Consulter son planning et
        celui de son équipe n'est pas un acte de gestion comptable — c'est le
        socle commun à toutes les briques. La comptabilité était la seule à ne
        rien exposer à l'agent (retour mairie, 24/07).
      */}
      <Tab active={tab === 'presence'} onClick={() => setTab('presence')} icon={CalendarDays}>
        Planning
      </Tab>

      {estGestionCompta && (
        <Tab active={tab === 'tiers'} onClick={() => setTab('tiers')} icon={Users}>
          Annuaire des tiers
        </Tab>
      )}
      {/* Recette du 28/09 : les réglages de la brique DANS la brique, en dernier. */}
      {estGestionCompta && (
        <Tab active={tab === 'reglages'} onClick={() => setTab('reglages')} icon={Settings}>
          Réglages
        </Tab>
      )}
    </div>
  );
}
